import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ShortcutAction } from 'src/app/core/enums/shortcut-action.enum';
import {
  ShortcutItem,
  ToggleShortcutActiveResponse,
  UpdateShortcutRequest,
} from 'src/app/core/interfaces/shortcut.interface';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ShortcutSettingsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  getShortcuts(): Observable<ShortcutItem[]> {
    return this.http
      .post<unknown>(`${this.baseUrl}/Shortcut/List`, {})
      .pipe(map((response) => this.extractShortcutItems(response)));
  }

  updateShortcut(id: number, payload: UpdateShortcutRequest): Observable<ShortcutItem> {
    return this.http
      .put<unknown>(`${this.baseUrl}/Shortcut/${id}`, payload)
      .pipe(map((response) => this.extractSingleShortcutItem(response)));
  }

  toggleShortcutActive(id: number): Observable<ToggleShortcutActiveResponse> {
    return this.http
      .post<unknown>(`${this.baseUrl}/Shortcut/${id}/toggle-active`, {})
      .pipe(map((response) => this.extractToggleActiveResponse(response)));
  }

  private extractShortcutItems(response: unknown): ShortcutItem[] {
    const rawList = this.getRawItems(response);
    return rawList
      .map((item) => this.mapToShortcutItem(item))
      .filter((item): item is ShortcutItem => item !== null);
  }

  private extractSingleShortcutItem(response: unknown): ShortcutItem {
    if (!this.isRecord(response)) {
      throw new Error('Invalid shortcut update response');
    }

    const data = response['data'];
    if (this.isRecord(data)) {
      const mapped = this.mapToShortcutItem(data);
      if (mapped) {
        return mapped;
      }
    }

    const mappedRoot = this.mapToShortcutItem(response);
    if (mappedRoot) {
      return mappedRoot;
    }

    throw new Error('Invalid shortcut update response');
  }

  private extractToggleActiveResponse(response: unknown): ToggleShortcutActiveResponse {
    if (!this.isRecord(response)) {
      throw new Error('Invalid shortcut toggle response');
    }

    const data = response['data'];
    const source = this.isRecord(data) ? data : response;
    const id = this.toNumber(source['id']);
    const isActive = this.toBoolean(source['isActive']);

    if (id === null) {
      throw new Error('Invalid shortcut toggle response');
    }

    return { id, isActive };
  }

  private getRawItems(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter(this.isRecord);
    }

    if (!this.isRecord(response)) {
      return [];
    }

    const data = response['data'];
    if (Array.isArray(data)) {
      return data.filter(this.isRecord);
    }

    return [];
  }

  private mapToShortcutItem(item: Record<string, unknown>): ShortcutItem | null {
    const id = this.toNumber(item['id']);
    const action = this.parseShortcutAction(item['action']);
    if (id === null || action === null) {
      return null;
    }

    const actionName =
      this.toOptionalString(item['actionName']) ??
      this.toOptionalString(item['action']) ??
      ShortcutAction[action] ??
      '';

    const keys =
      this.toOptionalString(item['keys']) ??
      this.toOptionalString(item['key']) ??
      this.toOptionalString(item['shortcutKeys']) ??
      this.toOptionalString(item['keyCombination']) ??
      '';

    return {
      id,
      action,
      actionName,
      keys,
      isActive: this.toBoolean(item['isActive']),
      serviceNameAr: this.toOptionalString(item['serviceNameAr']) ?? '',
      serviceNameEn: this.toOptionalString(item['serviceNameEn']) ?? '',
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private toNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private toString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toOptionalString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  private toBoolean(value: unknown): boolean {
    if (value === true) {
      return true;
    }

    if (value === false || value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    return false;
  }

  private parseShortcutAction(value: unknown): ShortcutAction | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return this.isShortcutAction(value) ? value : null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      const maybeNumeric = Number(trimmed);
      if (Number.isFinite(maybeNumeric) && this.isShortcutAction(maybeNumeric)) {
        return maybeNumeric;
      }

      const enumValue = ShortcutAction[trimmed as keyof typeof ShortcutAction];
      return typeof enumValue === 'number' ? enumValue : null;
    }

    return null;
  }

  private isShortcutAction(value: number): value is ShortcutAction {
    return (Object.values(ShortcutAction) as number[]).includes(value);
  }
}
